import { useState } from 'react';
import { Document, DocumentStatus, Folder } from '../../../../gql/graphql';
import { useUpload } from '../../../../contexts/UploadContext';
import { generateUniqueId } from '../../../../utils/file_util';
import { getExtension } from '../../../../utils/file_util';
import { FolderTreeItem } from '../../../../types/file';
import { determineUploadMode } from '../../../../utils/customArchive';

export const useFileUpload = (userId: string, refetchDocuments: () => void) => {
  const [isUploading, setIsUploading] = useState(false);
  const { uploadFolderTree, uploadFolderArchive, fileStatuses, clearFileStatuses, setFileStatus } = useUpload();

  const validateFile = (file: File): boolean => {
    const maxSize = 50 * 1024 * 1024; // 50MB
    const allowedTypes = ['.pdf', '.md'];

    if (file.size > maxSize) {
      throw new Error('File size exceeds 50MB limit');
    }

    const extension = getExtension(file.name).toLowerCase();
    if (!allowedTypes.includes(`.${extension}`)) {
      throw new Error(`File type .${extension} is not supported`);
    }

    return true;
  };

  const readFileAsBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        const base64Content = result.split(',')[1];
        resolve(base64Content);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const findOrCreateSubFolder = (parent: FolderTreeItem, folderName: string): FolderTreeItem => {
    let subFolder = parent.subFolders?.find((sf) => sf.name === folderName);
    if (!subFolder) {
      subFolder = {
        __typename: 'Folder',
        id: generateUniqueId(),
        name: folderName,
        description: null,
        parentId: parent.id,
        userId: parent.userId,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        agentFolders: [],
        subFolders: [],
        documents: []
      };
      parent.subFolders?.push(subFolder);
    }
    return subFolder;
  };

  const handleFileUpload = async (files: File[], folderId: string, selectedFolder: Folder) => {
    setIsUploading(true);
    clearFileStatuses();

    try {
      // Determine upload mode based on file count and size
      const uploadDecision = determineUploadMode(files);

      console.log('Upload decision:', {
        mode: uploadDecision.mode,
        fileCount: uploadDecision.fileCount,
        totalSize: `${(uploadDecision.totalSize / 1024 / 1024).toFixed(2)} MB`,
        useArchive: uploadDecision.useArchive,
        targetFolderId: folderId,
        selectedFolder: selectedFolder.name
      });

      // Route to optimized TAR archive upload if enabled
      if (uploadDecision.useArchive) {
        console.log('[Upload] Using optimized TAR archive mode');
        await uploadFolderArchive(files, folderId, selectedFolder);
        refetchDocuments();
        return;
      }

      // Fall back to legacy folder tree upload for small batches
      console.log('[Upload] Using legacy folder tree mode');

      const fileMap = new Map<string, { file: File; content: string }>();

      // Preprocess and store each file in a map
      for (const file of files) {
        try {
          if (!validateFile(file)) {
            throw new Error('Invalid file type or size');
          }
          const fileContent = await readFileAsBase64(file);
          const relativePath = file.webkitRelativePath || file.name;
          fileMap.set(relativePath, { file, content: fileContent });
        } catch (err) {
          const fileId = generateUniqueId();
          console.error(`Error processing file ${file.name}:`, err);
          setFileStatus(fileId, {
            progress: 0,
            isUploading: false,
            documentId: fileId,
            folderId: folderId,
            error: err instanceof Error ? err.message : 'Unknown error'
          });
          continue;
        }
      }

      // Create root folder structure
      const rootFolder: FolderTreeItem = {
        __typename: 'Folder',
        id: selectedFolder.id,
        name: selectedFolder.name,
        description: selectedFolder.description ?? null,
        parentId: selectedFolder.parentId ?? null,
        userId: selectedFolder.userId,
        createdAt: selectedFolder.createdAt,
        updatedAt: selectedFolder.updatedAt,
        agentFolders: [],
        subFolders: [],
        documents: []
      };

      // Process each file and create folder structure
      for (const [path, { file, content }] of fileMap) {
        const pathParts = path.split('/');
        const fileName = pathParts.pop() || '';
        let currentFolder = rootFolder;

        // Debug: Log path processing
        console.log(`Processing file: ${path}`);
        console.log(`Path parts after removing filename:`, pathParts);
        console.log(`Selected folder name: ${selectedFolder.name}`);

        // Create or descend into subfolders
        for (let i = 0; i < pathParts.length; i++) {
          const folderName = pathParts[i];
          console.log(`  Processing path part ${i}: "${folderName}"`);

          // Skip empty parts or if this is the root folder name and we're at the first level
          if (!folderName) {
            console.log(`    Skipping: empty folder name`);
            continue;
          }

          // Only skip if it's the first folder AND it matches the selected folder name
          if (i === 0 && folderName === selectedFolder.name) {
            console.log(`    Skipping: first folder matches selected folder name`);
            continue;
          }

          console.log(`    Creating/finding subfolder: "${folderName}" under "${currentFolder.name}"`);
          currentFolder = findOrCreateSubFolder(currentFolder, folderName);
        }

        // Create document
        const fileId = generateUniqueId();
        const doc: Document = {
          __typename: 'Document',
          id: fileId,
          title: fileName,
          extension: getExtension(fileName),
          fileSize: file.size,
          folderId: currentFolder.id,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          userId,
          status: DocumentStatus.Pending,
          source: 'upload',
          text: content,
          originalMarkdownMd5: null,
          processedMarkdownMd5: null,
          sourceDocumentMd5: null
        };
        currentFolder.documents?.push(doc);

        // Initialize file status
        setFileStatus(fileId, {
          progress: 0,
          isUploading: true,
          documentId: fileId,
          folderId: currentFolder.id
        });
      }

      // Debug: Log the folder structure before uploading
      console.log('Final folder structure before upload:', JSON.stringify(rootFolder, null, 2));
      console.log('Root folder details:', {
        id: rootFolder.id,
        name: rootFolder.name,
        parentId: rootFolder.parentId,
        subFoldersCount: rootFolder.subFolders?.length || 0,
        documentsCount: rootFolder.documents?.length || 0
      });

      // Upload folder tree
      await uploadFolderTree(rootFolder);

      // Update progress to 100% for all files
      rootFolder.documents?.forEach((doc) => {
        setFileStatus(doc.id, {
          progress: 100,
          isUploading: false,
          documentId: doc.id,
          folderId: doc.folderId
        });
      });

      refetchDocuments();
    } catch (error) {
      console.error('Error in folder upload process:', error);
      throw error;
    } finally {
      setIsUploading(false);
    }
  };

  return {
    isUploading,
    fileStatuses,
    handleFileUpload
  };
};